/**
 * Item detail — edit one item in place: title, description, category, status.
 * Writes go through useItemsStore.updateItem / retireItem (canonical paths).
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Archive, RotateCcw, PauseCircle } from 'lucide-react';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import type { ItemLanguageType } from '@/types/prioritization/lifecycle';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, Eyebrow, PrimaryButton, SecondaryButton, StatusChip } from '../components/primitives';

const CATEGORY_OPTIONS: Array<{ value: ItemLanguageType; label: string }> = [
  { value: 'problem', label: 'Problem' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'risk', label: 'Risk' },
  { value: 'solution', label: 'Solution' },
  { value: 'commitment', label: 'Commitment' },
  { value: 'investment', label: 'Investment' },
];

export default function ItemDetailScreen() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const app = getAppDefinition('items')!;

  const item = useItemsStore((s) => (itemId ? s.getItemById(itemId) : undefined));
  const updateItem = useItemsStore((s) => s.updateItem);
  const retireItem = useItemsStore((s) => s.retireItem);
  const fetchFromSupabase = useItemsStore((s) => s.fetchFromSupabase);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<string>('');
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!item) void fetchFromSupabase();
  }, [item, fetchFromSupabase]);

  useEffect(() => {
    if (item && !dirty) {
      setTitle(item.title);
      setDescription(item.description ?? '');
      setCategory(item.itemCategory ?? '');
    }
  }, [item, dirty]);

  if (!item) {
    return (
      <AppShell app={app} backTo="/apps/items">
        <EmptyState title="Loading item…" hint="If this doesn't resolve, the item may have been removed." />
      </AppShell>
    );
  }

  const status = item.exit_status ?? 'active';

  const save = () => {
    updateItem(item.id, {
      title: title.trim() || item.title,
      description: description.trim() || undefined,
      ...(category ? { itemCategory: category as ItemLanguageType } : {}),
    });
    setDirty(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const setStatus = (exit_status: 'active' | 'deferred') => {
    updateItem(item.id, { exit_status });
  };

  const archive = async () => {
    await retireItem(item.id, 'Archived from the Items app');
    navigate('/apps/items');
  };

  return (
    <AppShell app={app} backTo="/apps/items" backLabel="Back to items">
      <ScreenHeading title="Item" />
      <BallLine ballWith="you — edit or move it on" />
      <div className="space-y-4">
        <AppCard>
          <div className="mb-3 flex items-center justify-between">
            <Eyebrow>Details</Eyebrow>
            <StatusChip
              label={status === 'active' ? 'Active' : status === 'deferred' ? 'Set aside' : 'Archived'}
              tone={status === 'active' ? 'green' : 'neutral'}
            />
          </div>
          <label className="block font-body text-xs text-gray-500 dark:text-[var(--text-ter)]" htmlFor="item-title">
            Title
          </label>
          <input
            id="item-title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setDirty(true);
            }}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
          />
          <label className="mt-4 block font-body text-xs text-gray-500 dark:text-[var(--text-ter)]" htmlFor="item-description">
            Description
          </label>
          <textarea
            id="item-description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setDirty(true);
            }}
            rows={4}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
          />
          <label className="mt-4 block font-body text-xs text-gray-500 dark:text-[var(--text-ter)]" htmlFor="item-category">
            What kind of thing is it?
          </label>
          <select
            id="item-category"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setDirty(true);
            }}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
          >
            <option value="">Not sure yet</option>
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="mt-4 flex items-center gap-3">
            <PrimaryButton onClick={save} disabled={!dirty}>
              {saved ? 'Saved' : 'Save changes'}
            </PrimaryButton>
          </div>
        </AppCard>

        <AppCard>
          <Eyebrow>Move it on</Eyebrow>
          <div className="mt-3 flex flex-wrap gap-2">
            {status !== 'active' ? (
              <SecondaryButton onClick={() => setStatus('active')}>
                <RotateCcw className="h-4 w-4" /> Bring back
              </SecondaryButton>
            ) : (
              <SecondaryButton onClick={() => setStatus('deferred')}>
                <PauseCircle className="h-4 w-4" /> Set aside
              </SecondaryButton>
            )}
            {status !== 'archived' ? (
              <SecondaryButton onClick={() => void archive()}>
                <Archive className="h-4 w-4" /> Archive
              </SecondaryButton>
            ) : null}
          </div>
          <p className="mt-2 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
            Nothing is deleted — archived items stay in the record and can come back.
          </p>
        </AppCard>
      </div>
    </AppShell>
  );
}
